"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ArtistTopGlassScrollLayout = ArtistTopGlassScrollLayout;
var jsx_runtime_1 = require("react/jsx-runtime");
var material_1 = require("@mui/material");
var react_1 = require("react");
var colorUtils_1 = require("../../../utils/colorUtils");
var theme_1 = require("../../theme");
var tokens_1 = require("../../tokens");
/**
 * ArtistTop v2専用の固定ぼかし背景(Amazon Music風)スクロールレイアウト。
 *
 * レイヤー構造 (奥→手前):
 *   奥 (z:0 / sticky)  — ヒーロー画像をblur+tintスクリムで全幅に敷き、
 *                        スクロールしても動かず画面に固定される。
 *   中 (z:0 / absolute)— topScrim: ページ上端の白(darkは黒)グラデーション。
 *                        ページ側に固定されるのでスクロールで流れていく。
 *   手前 (z:1)         — children。この中のGlassCard等のbackdrop-filterは、
 *                        背後の固定ぼかし背景を透かしてさらにガラス化する。
 *
 * 背景の決まり方 (artistImageSrc/backgroundSrcの指定次第):
 * - どちらも無い: 背景画像レイヤー自体を描画せず、下地色(canvasColor)のみになる
 * - artistImageSrcのみ: そのアバター画像をぼかして表示 (スクリムは既定色)
 * - + theme.json(palette.background.default)が6桁hexで設定: スクリム色を
 *   アーティスト色に差し替える (ぼかした画像にアーティスト色のtintが乗る)
 * - backgroundSrcあり: artistImageSrc・色設定を無視し、ぼかし無しでそのまま表示
 *
 * スクロール検知: 先頭1pxセンチネルをIntersectionObserverで監視し、外れたら
 * scrolled=trueをrender-propで渡す(ArtistTopHeaderのガラス切替用)。
 * BaseLayoutのoverflowでwindowのstickyが効かないため、自前でheight:100dvhの
 * スクロールコンテナを持つ。
 *
 * スクロール可能領域: BaseLayoutのmaxWidthに閉じ込めると左右の余白でホイール
 * スクロールが反応しないため、`calc(50% - 50vw)`の負マージンで全幅にはみ出す。
 */
function ArtistTopGlassScrollLayout(_a) {
    var artistImageSrc = _a.artistImageSrc, backgroundSrc = _a.backgroundSrc, children = _a.children, maxWidth = _a.maxWidth, _b = _a.topScrim, topScrim = _b === void 0 ? false : _b;
    var p = (0, theme_1.useV2Palette)();
    var palette = (0, material_1.useTheme)().palette;
    var scrollRef = (0, react_1.useRef)(null);
    var sentinelRef = (0, react_1.useRef)(null);
    var _c = (0, react_1.useState)(false), scrolled = _c[0], setScrolled = _c[1];
    // backgroundSrc(デザイン済み背景)優先、無ければartistImageSrcをぼかして使う
    var resolvedSrc = backgroundSrc || artistImageSrc;
    var blurBackground = !backgroundSrc;
    // theme.jsonのpalette.background.default設定時は白ベタ部分を固有色に差し替える仕様
    var artistBackgroundColor = (0, colorUtils_1.isHex6)(palette.background.default)
        ? palette.background.default
        : undefined;
    var resolvedCanvasColor = artistBackgroundColor !== null && artistBackgroundColor !== void 0 ? artistBackgroundColor : p.background.base;
    var resolvedOverlayColor = artistBackgroundColor
        ? (0, material_1.alpha)(artistBackgroundColor, 0.8)
        : p.background.surface;
    // 先頭センチネルが上端から外れたら scrolled=true
    (0, react_1.useEffect)(function () {
        var _a;
        var el = sentinelRef.current;
        if (!el || typeof IntersectionObserver === 'undefined')
            return;
        var io = new IntersectionObserver(function (_a) {
            var entry = _a[0];
            return setScrolled(!entry.isIntersecting);
        }, { root: (_a = scrollRef.current) !== null && _a !== void 0 ? _a : null, threshold: 0 });
        io.observe(el);
        return function () { return io.disconnect(); };
    }, []);
    return ((0, jsx_runtime_1.jsxs)(material_1.Box, { ref: scrollRef, sx: {
            position: 'relative',
            // BaseLayoutのmaxWidthカラムに閉じ込めず、ビューポート全幅でホイールスクロールを受け付ける
            width: '100vw',
            marginLeft: 'calc(50% - 50vw)',
            height: '100dvh',
            overflowY: 'auto',
            overflowX: 'hidden',
            // iOSのラバーバンドで背景の届かない範囲まで見えてしまうのを防ぐ
            overscrollBehavior: 'contain',
            backgroundColor: resolvedCanvasColor,
            '&::-webkit-scrollbar': { display: 'none' },
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
        }, children: [resolvedSrc && ((0, jsx_runtime_1.jsxs)(material_1.Box, { "aria-hidden": true, sx: {
                    position: 'sticky',
                    top: 0,
                    height: '100dvh',
                    marginBottom: '-100dvh',
                    zIndex: 0,
                    overflow: 'hidden',
                    pointerEvents: 'none',
                }, children: [(0, jsx_runtime_1.jsx)(material_1.Box, { sx: __assign({ position: 'absolute', inset: blurBackground ? "-".concat(tokens_1.blur.backdrop, "px") : 0, backgroundImage: "url(".concat(resolvedSrc, ")"), backgroundSize: 'cover', backgroundPosition: 'center' }, (blurBackground && { filter: "blur(".concat(tokens_1.blur.backdrop, "px)") })) }), blurBackground && ((0, jsx_runtime_1.jsx)(material_1.Box, { sx: {
                            position: 'absolute',
                            inset: 0,
                            backgroundColor: resolvedOverlayColor,
                        } }))] })), blurBackground && topScrim && ((0, jsx_runtime_1.jsx)(material_1.Box, { "aria-hidden": true, sx: {
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '100vh',
                    '@supports (height: 100svh)': { height: '100svh' },
                    zIndex: 0,
                    pointerEvents: 'none',
                    backgroundImage: "linear-gradient(".concat(p.background.topScrimFrom, ", ").concat(p.background.topScrimTo, ")"),
                } })), (0, jsx_runtime_1.jsx)(material_1.Box, { sx: { maxWidth: maxWidth, mx: 'auto', position: 'relative', zIndex: 1 }, children: (0, jsx_runtime_1.jsxs)(material_1.Box, { sx: {
                        position: 'relative',
                        zIndex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        minHeight: '100%',
                    }, children: [(0, jsx_runtime_1.jsx)(material_1.Box, { ref: sentinelRef, sx: { position: 'absolute', top: 0, left: 0, height: '1px' } }), typeof children === 'function' ? children({ scrolled: scrolled }) : children] }) })] }));
}
